<template lang="pug">
  y-dialog(v-if="showDialog" v-model="showDialog" persistent fullscreen scrollable)
    v-card
      v-card-title.px-4
        v-btn(icon color="neutral700" @click="closeDialog")
          v-icon mdi-arrow-left
        v-spacer
        .text-md.font-semibold.ml-n4 New customer
        v-spacer
      v-divider
      v-card-text
        div.text-sm.font-semibold.neutral700--text.mt-5 Phone number
        phone-input.mt-2(v-model="phone" ref="phoneInput" required disabled)
        template(v-if="isNameEnabled")
          div.text-sm.font-semibold.neutral700--text.mt-5 Name&nbsp;
            span(v-if="!isNameRequired") (optional)
          v-text-field.text-sm.mt-2.round-xl(v-model="name" placeholder="Enter name here" outlined dense height="48" :rules="isNameRequired? rules.not_empty : []" hide-details="auto")
        template(v-if="isEmailEnabled")
          div.text-sm.font-semibold.neutral700--text.mt-5 Email&nbsp;
            span(v-if="!isEmailRequired") (optional)
          v-text-field.text-sm.mt-2.round-xl(v-model="email" placeholder="Enter email here" outlined dense height="48" hide-details="auto" :rules="isEmailRequired ? rules.email : email && email.length > 0 ? rules.email : []" type="email")
        template(v-if="loyaltySettings.marketing.plan === 'standalone'")
          div.text-sm.font-semibold.neutral700--text.mt-4 {{ $t('date_of_birth') }}
          v-text-field.text-sm.mt-2.round-xl(v-model="dob" placeholder="Enter date of birth" type="date" outlined dense height="48" hide-details="auto" :rules="rules.not_empty")
        div.text-sm.font-semibold.neutral700--text.mt-5 Purchased value (optional)
        v-text-field.text-sm.mt-2.round-xl(v-model="purchasedValue" placeholder="Enter purchased value here" outlined dense height="48" hide-details="auto" :prefix="$currency.symbol" type="number" @input="updatePurchasedValue($event)")
        stamp-to-collect.mt-10(v-if="loyaltySystem === 'stamps'" v-model="collectValue" :value-collected="0")
        template(v-else)
          .text-md.font-semibold.neutral700--text.mt-10 How many points you would like to collect?
          v-text-field.text-sm.mt-2.round-xl(v-model="collectValue" outlined dense height="48" hide-details="auto" suffix="points" type="number")
        v-btn.button-md.mt-16.white--text(block depressed color="neutral700" :loading="isLoading" :disabled="!canProceed" @click="handleSave") Save and collect
</template>

<script>
import PhoneInput from '@/components/clinic/custom/PhoneInput.vue'
import StampToCollect from "@/components/marketing/merchantapp/stamps/StampToCollect.vue";
import {mapActions, mapGetters} from "vuex";

export default {
  components: {
    StampToCollect,
    PhoneInput
  },
  props: {
    open: {
      type: Boolean,
    },
    phone: {
      type: String,
      default: null
    },
    loyaltySystem: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      isLoading: false,
      name: null,
      email: null,
      dob: null,
      purchasedValue: null,
      collectValue: 1,
      rules: {
        not_empty: [v => (v != null && v.toString().trim().length) || 'This field is required'],
        email: [v => /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(v) || 'E-mail must be valid',]
      }
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      loyaltySettings: 'marketing/getLoyaltySettings'
    }),
    showDialog: {
      get() {
        return this.open
      },
      set(newValue) {
        this.$emit('update:open', newValue)
      }
    },
    canProceed() {
      let baseValidation = Boolean(this.phone)
      if (this.isNameEnabled && this.isNameRequired) {
        baseValidation = baseValidation && Boolean(this.name)
      }
      if (this.isEmailEnabled && this.isEmailRequired) {
        baseValidation = baseValidation && Boolean(this.email)
      }
      if (this.loyaltySettings.marketing.plan === 'standalone') {
        baseValidation = baseValidation && Boolean(this.dob)
      }
      return baseValidation
    },
    isNameEnabled() {
      return Boolean(this.loyaltySettings?.page_properties?.find(prop => prop.name === 'Name')?.enabled)
    },
    isEmailEnabled() {
      return Boolean(this.loyaltySettings?.page_properties?.find(prop => prop.name === 'Email')?.enabled)
    },
    isNameRequired() {
      return Boolean(this.loyaltySettings.page_properties.find(prop => prop.name === 'Name')?.required)
    },
    isEmailRequired() {
      return Boolean(this.loyaltySettings.page_properties.find(prop => prop.name === 'Email')?.required)
    },
    purchaseValuePriceInteger() {
      const purchasedValue = this.purchasedValue
      if (!purchasedValue) return null
      return parseInt(parseFloat(purchasedValue).toFixed(this.$currency.exponent) * this.$currency.multiplier)
    },
    createPayload() {
      let payload = {
        name: this.name,
        phone: this.phone,
        email: this.email,
      }
      if (this.loyaltySettings.marketing.plan === 'standalone') {
        payload.dob = this.dob
      }
      return payload
    },
    collectValuePayload() {
      let payload = {
        collect_value: this.collectValue,
        type: this.loyaltySystem
      }
      if (this.purchasedValue) {
        payload.purchased_value = this.purchaseValuePriceInteger
      }
      return payload
    },
    pointsEarned() {
      return this.loyaltySettings?.points_earned || 1
    }
  },
  methods: {
    ...mapActions([
      'showMessage',
    ]),
    closeDialog() {
      this.showDialog = false
      this.$emit('close')
    },
    handleCreateMember() {
      const url = `/loyalty/${this.company.slug}/members`
      return this.$axios.post(url, this.createPayload)
          .then(async ({data}) => {
            this.updateActivityLog(url, this.createPayload, data, 'POST', 'Create member')
            await this.handleCollectValue(data.id)
          })
    },
    handleCollectValue(id) {
      const url = `/loyalty/${this.company.slug}/members/${id}/collects`
      return this.$axios.post(url, this.collectValuePayload)
          .then(({data}) => {
            this.updateActivityLog(url, this.collectValuePayload, data, 'POST', 'Collect value')
          })
    },
    async handleSave() {
      try {
        this.isLoading = true
        await this.handleCreateMember()
        this.showMessage(['success', 'Member created successfully'])
        this.closeDialog()
      } catch (e) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isLoading = false
      }
    },
    updatePurchasedValue(value) {
      if (this.loyaltySystem === 'points') {
        this.collectValue = Math.ceil(value * this.pointsEarned)
      }
    },
    updateActivityLog(endpoint, payload, response, method, activity, id) {
      let data = {
        company: this.company.id,
        object_id: id || null,
        object_ref: 'Create new customer',
        activity: `Merchant-app: ${activity}`,
        raw_data: {endpoint, payload, response, method}
      }
      this.$axios.post('/users/logs', data)
          .catch(() => {
          })
    },
  },
}
</script>

<style scoped>
.v-text-field--outlined >>> fieldset {
  border-color: var(--v-primary200);
}

.error--text >>> fieldset {
  border-color: var(--v-danger);
}
</style>
